/**
 * Exchange Slice - Handles exchange phase state changes
 *
 * Actions handled:
 * - SET_EXCHANGE_CURRENT_PLAYER
 * - SET_EXCHANGE_SELECTION
 * - SET_EXCHANGE_READY
 * - SET_EXCHANGE_READY_UPDATER
 * - SET_SHOW_EXCHANGE
 * - SET_EXCHANGE_INFO
 * - SET_CARDS_PER_PLAYER
 * - SET_EXCHANGE_CARDS_DEALT
 * - SET_EXCHANGE_STATE
 * - UPDATE_EXCHANGE_STATE
 *
 * Seal Distribution Actions (manuelle Siegel-Platzierung):
 * - SEAL_DISTRIBUTION_STARTED
 * - SEAL_PLACED
 * - SEAL_REMOVED
 * - PLAYER_SEAL_READY
 * - SEAL_DISTRIBUTION_COMPLETE
 * - SEAL_DISTRIBUTION_TIMEOUT
 */

// Initial exchange state (extracted from initialGameState)
export const initialExchangeState = {
  exchangeState: null,
  exchangeReady: [],
  exchangeSelections: {},
  exchangeCurrentPlayer: 0,
  selectedCardsForExchange: [],
  showExchange: false,
  exchangeInfo: null,
  cardsPerPlayer: 13,
  exchangeCardsDealt: false,
  // Seal Distribution
  sealDistribution: null,
  sealDistributionPlacements: [], // Array von { sealId, cardIndex }
};

/**
 * Exchange action types
 */
export const EXCHANGE_ACTIONS = [
  'SET_EXCHANGE_CURRENT_PLAYER',
  'SET_EXCHANGE_SELECTION',
  'SET_EXCHANGE_READY',
  'SET_EXCHANGE_READY_UPDATER',
  'SET_SHOW_EXCHANGE',
  'SET_EXCHANGE_INFO',
  'SET_CARDS_PER_PLAYER',
  'SET_EXCHANGE_CARDS_DEALT',
  'SET_EXCHANGE_STATE',
  'UPDATE_EXCHANGE_STATE',
  // Seal Distribution
  'SEAL_DISTRIBUTION_STARTED',
  'SEAL_DISTRIBUTION_TIMER_RESET',
  'SEAL_PLACED',
  'SEAL_REMOVED',
  'PLAYER_SEAL_READY',
  'SEAL_DISTRIBUTION_COMPLETE',
  'SEAL_DISTRIBUTION_TIMEOUT',
];

/**
 * Check if action is handled by this slice
 */
export function isExchangeAction(actionType) {
  return EXCHANGE_ACTIONS.includes(actionType);
}

/**
 * Exchange reducer slice
 */
export function exchangeReducer(state, action) {
  switch (action.type) {
    case 'SET_EXCHANGE_CURRENT_PLAYER':
      return {
        ...state,
        exchangeCurrentPlayer: action.payload,
      };

    case 'SET_EXCHANGE_SELECTION':
      return {
        ...state,
        selectedCardsForExchange: action.payload,
      };

    case 'SET_EXCHANGE_READY':
      // Support legacy format with playerIndex/ready
      if (action.payload?.playerIndex !== undefined) {
        const newExchangeReady = [...state.exchangeReady];
        newExchangeReady[action.payload.playerIndex] = action.payload.ready;
        return {
          ...state,
          exchangeReady: newExchangeReady,
        };
      }
      // Direct array assignment
      return {
        ...state,
        exchangeReady: action.payload,
      };

    case 'SET_EXCHANGE_READY_UPDATER': {
      // Support updater function pattern: (prev) => newValue
      const updatedReady = action.payload(state.exchangeReady);
      return {
        ...state,
        exchangeReady: updatedReady,
      };
    }

    case 'SET_SHOW_EXCHANGE':
      return {
        ...state,
        showExchange: action.payload,
      };

    case 'SET_EXCHANGE_INFO':
      return {
        ...state,
        exchangeInfo: action.payload,
      };

    case 'SET_CARDS_PER_PLAYER':
      return {
        ...state,
        cardsPerPlayer: action.payload,
      };

    case 'SET_EXCHANGE_CARDS_DEALT':
      return {
        ...state,
        exchangeCardsDealt: action.payload,
      };

    case 'SET_EXCHANGE_STATE':
      return {
        ...state,
        exchangeState: action.payload,
      };

    case 'UPDATE_EXCHANGE_STATE':
      return {
        ...state,
        exchangeState: state.exchangeState
          ? { ...state.exchangeState, ...action.payload }
          : action.payload,
      };

    // =============================================
    // SEAL DISTRIBUTION ACTIONS
    // =============================================

    case 'SEAL_DISTRIBUTION_STARTED':
      return {
        ...state,
        sealDistribution: {
          active: true,
          startTime: action.payload.startTime,
          duration: action.payload.duration,
          readyPlayers: action.payload.readyPlayers || [],
        },
        // Reset lokale Placements
        sealDistributionPlacements: [],
        // Update stash und sealedCards falls mitgesendet
        stash: action.payload.stash ?? state.stash,
        sealedCards: action.payload.sealedCards ?? state.sealedCards,
      };

    case 'SEAL_PLACED':
      // Array statt Object, damit mehrere gleiche Siegel-IDs möglich sind
      return {
        ...state,
        sealDistributionPlacements: [
          ...state.sealDistributionPlacements,
          { sealId: action.payload.sealId, cardIndex: action.payload.cardIndex },
        ],
      };

    case 'SEAL_REMOVED': {
      // Entferne nur das ERSTE Vorkommen dieser sealId (wie Server)
      const idx = state.sealDistributionPlacements.findIndex(
        p => p.sealId === action.payload.sealId
      );
      if (idx === -1) return state;
      const newPlacements = [...state.sealDistributionPlacements];
      newPlacements.splice(idx, 1);
      return {
        ...state,
        sealDistributionPlacements: newPlacements,
      };
    }

    case 'PLAYER_SEAL_READY':
      return {
        ...state,
        sealDistribution: state.sealDistribution
          ? {
              ...state.sealDistribution,
              readyPlayers: action.payload.readyPlayers ||
                (state.sealDistribution.readyPlayers.includes(action.payload.playerIndex)
                  ? state.sealDistribution.readyPlayers
                  : [...state.sealDistribution.readyPlayers, action.payload.playerIndex]),
            }
          : state.sealDistribution,
      };

    case 'SEAL_DISTRIBUTION_TIMER_RESET':
      // Reset timer for new player in test mode
      return {
        ...state,
        sealDistribution: state.sealDistribution
          ? {
              ...state.sealDistribution,
              startTime: action.payload.startTime,
              duration: action.payload.duration,
            }
          : state.sealDistribution,
        // Clear local placements when switching to new player
        sealDistributionPlacements: [],
      };

    case 'SEAL_DISTRIBUTION_COMPLETE':
      return {
        ...state,
        sealDistribution: { active: false },
        sealDistributionPlacements: [],
        // Update sealedCards und stash mit finalen Werten
        sealedCards: action.payload?.sealedCards ?? state.sealedCards,
        stash: action.payload?.stash ?? state.stash,
      };

    case 'SEAL_DISTRIBUTION_TIMEOUT':
      // Identisch mit COMPLETE - Timer ist abgelaufen
      // Server sendet finale sealedCards/stash Werte
      return {
        ...state,
        sealDistribution: { active: false },
        sealDistributionPlacements: [],
        // Update sealedCards und stash mit finalen Werten (falls vorhanden)
        sealedCards: action.payload?.sealedCards ?? state.sealedCards,
        stash: action.payload?.stash ?? state.stash,
      };

    default:
      return state;
  }
}

export default exchangeReducer;
